import React, { useMemo, useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  GripVertical, MoreVertical, Pencil, Trash2, Printer, CalendarClock, Calendar, MapPin,
  BedDouble, Car, TrainFront, Plane, Ticket, Sun, Users, Clock, ChevronDown, ChevronRight,
  Flag, LayoutTemplate, Wand2, Loader2, Check,
} from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { api, apiErrorMessage } from "@/lib/api";
import {
  RESERVA_TYPE_LABELS, RESERVA_STATUS_LABELS, RESERVA_STATUS_CLASS,
  RESERVA_FACTURACION_LABELS, RESERVA_TRANSITIONS, fmtEUR, reservaQty, reservaCotizadoTotal,
} from "@/pages/trips/shared";
import { openVoucher } from "@/pages/trips/documents";

const TYPE_ICON = {
  alojamiento: BedDouble, actividad: MapPin, entradas: Ticket,
  transfer: Car, tren: TrainFront, vuelo: Plane, free_day: Sun,
};
// Chip de proveedor: un solo color gris oscuro para todos (petición Dirección, oct-2026).
// Solo «sin proveedor» va más claro, como aviso de dato pendiente.
const COLOR_PLACEHOLDER = "#94a3b8";
const COLOR_PROVIDER = "#4b5563";
const providerChipColor = (r) => (r.provider_name ? COLOR_PROVIDER : COLOR_PLACEHOLDER);

const GROUP_TYPE_LABELS = { private: "Private", small_group: "Small group" };
// Modalidad: campo `group_type`; si falta, se deduce del nombre/nota («private», «small group», «shared»).
const inferGroupType = (r) => {
  if (r.group_type) return r.group_type;
  const txt = `${r.service || ""} ${r.note || ""}`.toLowerCase();
  if (/small[\s-]?group|shared|join[\s-]?in|group tour/.test(txt)) return "small_group";
  if (/\bprivate\b|privado|privada/.test(txt)) return "private";
  return null;
};
const BOARD_LABELS = { sa: "Room Only", bb: "Bed & Breakfast", mp: "Half Board", pc: "Full Board" };
const ROOM_LABELS = { single: "Single", individual: "Single", doble: "Double", twin: "Twin", triple: "Triple", cuadruple: "Quad", suite: "Suite", family: "Family", otro: "Room" };
// «2 × Double · Single» desde room_items; si no hay, el texto libre `room`.
const roomTypesLabel = (r) => {
  const items = r.room_items || [];
  if (items.length) {
    const counts = items.reduce((m, it) => { const k = ROOM_LABELS[(it.room_type || "doble").toLowerCase()] || it.room_type || "Room"; m[k] = (m[k] || 0) + 1; return m; }, {});
    return Object.entries(counts).map(([k, n]) => (n > 1 ? `${n} × ${k}` : k)).join(" · ");
  }
  return (r.room || "").trim();
};

const fmtShort = (iso) => {
  if (!iso) return "";
  const d = new Date(iso.slice(0, 10));
  return isNaN(d) ? iso : d.toLocaleDateString("es-ES", { day: "numeric", month: "short" });
};
const nightsBetween = (a, b) => {
  if (!a || !b) return 0;
  const n = Math.round((new Date(b.slice(0, 10)) - new Date(a.slice(0, 10))) / 86400000);
  return n > 0 ? n : 0;
};

/**
 * Selector inline de proveedor. Muestra el chip actual; al hacer clic abre
 * un popover con buscador. En blur guarda vía PUT y refresca la reserva.
 */
function InlineProviderPicker({ tripId, reserva, chipColor }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const [q, setQ] = useState("");
  React.useEffect(() => { const t = setTimeout(() => setQ(term), 250); return () => clearTimeout(t); }, [term]);

  const { data } = useQuery({
    queryKey: ["provider-search-inline", q],
    queryFn: async () => (await api.get("/providers", { params: { q, solo_viaje: true, limit: 20 } })).data,
    enabled: open && q.trim().length >= 1,
    keepPreviousData: true,
  });
  const items = data?.items || [];

  const mut = useMutation({
    mutationFn: (payload) => api.put(`/trips/${tripId}/reservas/${reserva.id}`, payload),
    onSuccess: () => { toast.success("Proveedor actualizado"); qc.invalidateQueries({ queryKey: ["trip", tripId] }); setOpen(false); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const pick = (p) => {
    mut.mutate({ provider_id: p.id, provider_name: p.nombre_comercial || p.name });
  };
  const clear = () => {
    mut.mutate({ provider_id: null, provider_name: "" });
  };

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) setTerm(reserva.provider_name || ""); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-block max-w-full truncate rounded-md px-2 py-0.5 text-[11px] font-semibold text-white hover:opacity-90"
          style={{ background: chipColor }}
          title={reserva.provider_name ? `Click para cambiar proveedor · ${reserva.provider_sistema || ""}` : "Click para asignar proveedor"}
          data-testid={`inline-provider-btn-${reserva.id}`}
        >
          {reserva.provider_name || "sin proveedor"}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[360px] p-0" onClick={(e) => e.stopPropagation()}>
        <div className="border-b p-2">
          <Input
            autoFocus
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            onFocus={(e) => e.target.select()}
            placeholder="Buscar proveedor…"
            className="h-8 text-sm"
            data-testid={`inline-provider-input-${reserva.id}`}
          />
        </div>
        <div className="max-h-64 overflow-auto p-1">
          {mut.isPending && <div className="px-2 py-1.5 text-xs text-muted-foreground"><Loader2 className="mr-1 inline h-3 w-3 animate-spin" />Guardando…</div>}
          {!mut.isPending && q.trim().length < 1 && (
            <div className="px-2 py-1.5 text-xs text-muted-foreground">Escribe al menos 1 letra para buscar.</div>
          )}
          {!mut.isPending && q.trim().length >= 1 && items.length === 0 && (
            <div className="px-2 py-1.5 text-xs text-muted-foreground">Sin coincidencias.</div>
          )}
          {items.map((p) => (
            <button
              type="button"
              key={p.id}
              onClick={() => pick(p)}
              className="flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-accent"
              data-testid={`inline-provider-opt-${reserva.id}-${p.id}`}
            >
              <span className="flex items-center gap-2">
                <Check className={`h-3 w-3 ${p.id === reserva.provider_id ? "opacity-100" : "opacity-0"}`} />
                <span className="truncate font-medium">{p.nombre_comercial || p.name}</span>
              </span>
              {p.country && <span className="text-xs text-muted-foreground">{p.country}</span>}
            </button>
          ))}
        </div>
        {reserva.provider_id && (
          <div className="border-t p-2">
            <button
              type="button"
              onClick={clear}
              className="w-full rounded px-2 py-1 text-left text-xs text-destructive hover:bg-destructive/10"
              data-testid={`inline-provider-clear-${reserva.id}`}
            >
              Quitar proveedor
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function StateChips({ r }) {
  const fact = r.status_facturacion || "sin_factura";
  // Estado TD tri-estado (2026-08):
  //   • "NO TD"  → el operador marcó `exclude_from_td` → no aparece en el TD público
  //   • "TD+"   → tiene plantilla asignada o descripción propia → listo para el cliente
  //   • "TD-"   → sin nada aún → hay que trabajarlo
  let tdBadge;
  if (r.exclude_from_td) {
    tdBadge = { label: "NO TD", cls: "bg-rose-100 text-rose-700 border border-rose-200", title: "Reserva excluida del Travel Document" };
  } else if (r.plantilla_id || (r.sales_description || "").trim().length > 20) {
    tdBadge = { label: "TD+", cls: "bg-emerald-100 text-emerald-700 border border-emerald-200", title: "Travel Document listo (plantilla asignada o descripción propia)" };
  } else {
    tdBadge = { label: "TD-", cls: "bg-amber-100 text-amber-700 border border-amber-200", title: "Sin plantilla ni descripción · falta por rellenar" };
  }
  return (
    <div className="flex flex-wrap items-center gap-1">
      {r.booked_by_kimkim && (
        <span className="rounded bg-orange-100 px-1.5 py-0.5 text-[10px] font-semibold text-orange-700" title="Lo reserva KimKim · no contactar proveedor">BOOKED BY KIMKIM</span>
      )}
      <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${RESERVA_STATUS_CLASS[r.status_reserva]}`}>
        {(RESERVA_STATUS_LABELS[r.status_reserva] || "").toUpperCase()}
      </span>
      <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${fact === "sin_factura" ? "bg-slate-100 text-slate-500" : "bg-blue-100 text-blue-700"}`}>
        {fact === "sin_factura" ? "S/FACT." : (RESERVA_FACTURACION_LABELS[fact] || "FACTURA").toUpperCase()}
      </span>
      <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${r.status_pago ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
        {r.status_pago ? "PAGADO" : "S/PAGO"}
      </span>
      {r.status_conciliado && <span className="rounded bg-teal-100 px-1.5 py-0.5 text-[10px] font-semibold text-teal-700">HOLDED</span>}
      {r.status_proforma_voucher && <span className="rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold text-violet-700">PROFORMA</span>}
      {r.factura_solicitada && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">F. SOLIC.</span>}
      <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${tdBadge.cls}`} title={tdBadge.title} data-testid={`td-badge-${r.id}`}>{tdBadge.label}</span>
      {r.flag && <span className="flex items-center rounded bg-orange-100 px-1.5 py-0.5 text-[10px] font-semibold text-orange-700"><Flag className="h-2.5 w-2.5" /></span>}
    </div>
  );
}

export default function ReservasDayList({ tripId, trip, reservas, onOpen, onDelete, lastOpenedId }) {
  const qc = useQueryClient();
  const [collapsed, setCollapsed] = useState(new Set());
  const [dragId, setDragId] = useState(null);
  const [dragOverDay, setDragOverDay] = useState(null);

  // Al volver del detalle de una reserva, scrolleamos a esa fila para que el
  // operador siga trabajando desde el mismo punto en lugar de aparecer arriba
  // del todo. También le damos un highlight suave (~1.5s) para localizarla
  // visualmente. Corre solo si el usuario había abierto una reserva previamente.
  useEffect(() => {
    if (!lastOpenedId) return;
    // Delay pequeño para asegurar que el DOM está pintado tras el toggle.
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="reserva-row-${lastOpenedId}"]`);
      if (!el) return;
      el.scrollIntoView({ block: "center", behavior: "auto" });
      el.classList.add("ring-2", "ring-primary/50", "ring-offset-1", "transition-shadow");
      setTimeout(() => el.classList.remove("ring-2", "ring-primary/50", "ring-offset-1"), 1500);
    }, 50);
    return () => clearTimeout(t);
  }, [lastOpenedId]);

  const startD = trip.start_date ? new Date(trip.start_date.slice(0, 10)) : null;

  const effDay = (r) => {
    if (r.day && r.day >= 1) return r.day;
    if (r.date_entry && startD) {
      const d = Math.round((new Date(r.date_entry.slice(0, 10)) - startD) / 86400000) + 1;
      return d >= 1 ? d : 1;
    }
    return 0;
  };

  // Agrupar por día efectivo y ordenar dentro del día.
  // Regla de orden (04-ago-2026):
  //   • Día 100 % con hora → la HORA manda (auto-reorden al editar hora).
  //   • Día MIXTO (alojamientos sin hora + actividades con hora) o 100 % sin
  //     hora → `sort_index` manda para respetar el drag-drop manual (permite
  //     colocar un alojamiento en medio del día sin quedarse pegado al final).
  //   • Desempates cascada: hora → sort_index → created_at.
  const groups = useMemo(() => {
    const m = new Map();
    reservas.forEach((r) => { const d = effDay(r); if (!m.has(d)) m.set(d, []); m.get(d).push(r); });
    const arr = [...m.entries()].map(([day, rows]) => {
      const hasHourless = rows.some((r) => !r.hour_entry);
      rows.sort((a, b) => {
        const ha = a.hour_entry || "";
        const hb = b.hour_entry || "";
        const sa = a.sort_index ?? 9999;
        const sb = b.sort_index ?? 9999;
        if (hasHourless) {
          // Manda el orden manual (drag-drop) para permitir reordenar los sin-hora.
          if (sa !== sb) return sa - sb;
          if (ha && !hb) return -1;
          if (!ha && hb) return 1;
          if (ha && hb && ha !== hb) return ha.localeCompare(hb);
        } else {
          // Todos con hora → auto-orden por hora.
          if (ha !== hb) return ha.localeCompare(hb);
          if (sa !== sb) return sa - sb;
        }
        return (a.created_at || "").localeCompare(b.created_at || "");
      });
      return { day, rows };
    });
    arr.sort((a, b) => (a.day === 0 ? 9999 : a.day) - (b.day === 0 ? 9999 : b.day));
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservas, trip.start_date]);

  const maxDay = Math.max(0, ...groups.map((g) => g.day));

  const reorderMut = useMutation({
    mutationFn: (items) => api.put(`/trips/${tripId}/reservas/reorder`, { items }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["trip", tripId] }),
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const statusMut = useMutation({
    mutationFn: ({ id, status_reserva }) => api.patch(`/trips/${tripId}/reservas/${id}/status`, { status_reserva }),
    onSuccess: () => { toast.success("Estado actualizado"); qc.invalidateQueries({ queryKey: ["trip", tripId] }); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const buildItems = (targetDay, targetIndex) => {
    const map = new Map();
    groups.forEach((g) => map.set(g.day, g.rows.map((r) => r.id)));
    for (const [d, ids] of map) map.set(d, ids.filter((id) => id !== dragId));
    if (!map.has(targetDay)) map.set(targetDay, []);
    const arr = map.get(targetDay);
    const idx = targetIndex == null ? arr.length : Math.max(0, Math.min(targetIndex, arr.length));
    arr.splice(idx, 0, dragId);
    const items = [];
    for (const [d, ids] of map) ids.forEach((id, i) => items.push({ id, day: d, sort_index: i }));
    return items;
  };

  const drop = (targetDay, targetIndex) => {
    if (!dragId || targetDay < 1) { setDragId(null); setDragOverDay(null); return; }
    reorderMut.mutate(buildItems(targetDay, targetIndex));
    setDragId(null); setDragOverDay(null);
  };

  const moveToDay = (r, targetDay) => {
    setDragId(r.id);
    setTimeout(() => {
      const items = buildItemsFor(r.id, targetDay, null);
      reorderMut.mutate(items);
      setDragId(null);
    }, 0);
  };
  const buildItemsFor = (id, targetDay, targetIndex) => {
    const map = new Map();
    groups.forEach((g) => map.set(g.day, g.rows.map((x) => x.id)));
    for (const [d, ids] of map) map.set(d, ids.filter((x) => x !== id));
    if (!map.has(targetDay)) map.set(targetDay, []);
    const arr = map.get(targetDay);
    arr.splice(targetIndex == null ? arr.length : targetIndex, 0, id);
    const items = [];
    for (const [d, ids] of map) ids.forEach((x, i) => items.push({ id: x, day: d, sort_index: i }));
    return items;
  };

  const toggleDay = (d) => setCollapsed((p) => { const n = new Set(p); n.has(d) ? n.delete(d) : n.add(d); return n; });

  // Preasignación masiva de plantillas TD
  const [preasignarOpen, setPreasignarOpen] = useState(false);
  const [preasignarResult, setPreasignarResult] = useState(null);
  const [preasignarOverwrite, setPreasignarOverwrite] = useState(false);

  const stats = useMemo(() => {
    const total = reservas.length;
    const withPlantilla = reservas.filter((r) => r.plantilla_id).length;
    return { total, withPlantilla, missing: total - withPlantilla };
  }, [reservas]);

  const preasignarMut = useMutation({
    mutationFn: async ({ overwrite }) =>
      (await api.post(`/trips/${tripId}/reservas/preasignar-plantillas`, {
        only_missing: !overwrite,
        overwrite,
      })).data,
    onSuccess: (data) => {
      setPreasignarResult(data);
      qc.invalidateQueries({ queryKey: ["trip", tripId] });
      if (data.assigned > 0) {
        toast.success(`${data.assigned} plantilla(s) asignada(s)`);
      } else {
        toast.info("Ninguna plantilla nueva asignada");
      }
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const dayHeaderDate = (day) => {
    if (!startD || day < 1) return null;
    const d = new Date(startD); d.setDate(d.getDate() + (day - 1));
    const s = d.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" });
    return s.charAt(0).toUpperCase() + s.slice(1);
  };
  const dayCity = (rows) => { const c = rows.map((r) => r.city).filter(Boolean); return c[0] || ""; };

  return (
    <div className="space-y-3" data-testid="reservas-day-list">
      {/* Barra superior: KPIs + acciones masivas TD */}
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-card px-3 py-2">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <LayoutTemplate className="h-3.5 w-3.5 text-primary" />
          <span className="font-medium">Plantillas TD:</span>
          <Badge variant="secondary" data-testid="reservas-td-stats">
            {stats.withPlantilla} / {stats.total} asignadas
          </Badge>
          {stats.missing > 0 && (
            <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800">
              {stats.missing} sin plantilla
            </Badge>
          )}
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => { setPreasignarResult(null); setPreasignarOverwrite(false); setPreasignarOpen(true); }}
          disabled={!reservas.length}
          data-testid="reservas-preasignar-td-btn"
        >
          <Wand2 className="mr-1.5 h-3.5 w-3.5" />
          Preasignar plantillas TD
        </Button>
      </div>
      {groups.map(({ day, rows }) => {
        const isCol = collapsed.has(day);
        const isDateless = day === 0;
        const headerDate = dayHeaderDate(day);
        return (
          <Card key={day} className={`overflow-hidden ${dragOverDay === day ? "ring-2 ring-primary" : ""}`} data-testid={`day-group-${day}`}>
            <button
              onClick={() => toggleDay(day)}
              className="flex w-full items-center gap-3 border-b bg-muted/40 px-4 py-2.5 text-left transition-colors hover:bg-muted/70"
              data-testid={`day-header-${day}`}
              onDragOver={(e) => { if (dragId && !isDateless) { e.preventDefault(); setDragOverDay(day); } }}
              onDrop={(e) => { e.preventDefault(); drop(day, null); }}
            >
              {isCol ? <ChevronRight className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
              {isDateless ? (
                <span className="rounded-lg bg-slate-400 px-2.5 py-1 text-xs font-bold text-white">Sin día</span>
              ) : (
                <span className="rounded-lg bg-primary px-2.5 py-1 text-xs font-bold text-primary-foreground">Día {day}</span>
              )}
              <Calendar className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold">{headerDate || "Sin fecha asignada"}</span>
              {dayCity(rows) && <span className="flex items-center gap-1 text-xs text-muted-foreground"><MapPin className="h-3 w-3" />{dayCity(rows)}</span>}
              <span className="text-xs text-muted-foreground">· {rows.length} servicio{rows.length !== 1 ? "s" : ""}</span>
            </button>

            {!isCol && (
              <div>
                {/* cabecera de columnas · orden: Hora · Pax · Servicio · Proveedor · Estado · Cotizado · Coste
                    (Fecha se ha eliminado porque ya aparece en el header del día;
                     para alojamientos, check-in/check-out se muestra como sub-línea del Servicio) */}
                <div className="grid items-center gap-3 border-b px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70"
                  style={{ gridTemplateColumns: "20px 52px 80px minmax(240px,1.4fr) 150px 1fr 90px 90px 32px" }}>
                  <span></span><span>Hora</span><span>Pax</span><span>Servicio</span><span>Proveedor</span><span>Estado</span><span className="text-right">Cotizado</span><span className="text-right">Coste</span><span></span>
                </div>

                {rows.map((r, idx) => {
                  const Icon = TYPE_ICON[r.type] || MapPin;
                  // Pax por reserva: si `r.quantity` está seteado y ≠ 2 (default),
                  // úsalo (permite que una actividad tenga menos pax que el viaje).
                  // Si no, cae al pax global del viaje.
                  const ad = r.quantity ?? trip.adults ?? trip.number_of_travelers ?? 1;
                  const ch = r.number_of_children ?? trip.number_of_children ?? 0;
                  const pax = ch > 0 ? `${ad} AD · ${ch} CH` : `${ad} AD`;
                  const isHotel = r.type === "alojamiento";
                  const n = nightsBetween(r.date_entry, r.date_exit);
                  const sistemaColor = providerChipColor(r);
                  return (
                    <div
                      key={r.id}
                      draggable
                      onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; setDragId(r.id); }}
                      onDragEnd={() => { setDragId(null); setDragOverDay(null); }}
                      onDragOver={(e) => { if (dragId) { e.preventDefault(); setDragOverDay(day); } }}
                      onDrop={(e) => { e.preventDefault(); e.stopPropagation(); drop(day, idx); }}
                      className={`border-b transition-colors last:border-b-0 hover:bg-muted/40 ${dragId === r.id ? "opacity-40" : ""}`}
                      data-testid={`reserva-row-${r.id}`}
                    >
                      <div className="grid items-center gap-3 px-4 py-2.5"
                        style={{ gridTemplateColumns: "20px 52px 80px minmax(240px,1.4fr) 150px 1fr 90px 90px 32px" }}>
                        <span className="cursor-grab text-muted-foreground/50 hover:text-foreground" title="Arrastrar para reordenar / mover de día"><GripVertical className="h-4 w-4" /></span>
                        <span className="flex items-center gap-1 text-xs text-muted-foreground"><Clock className="h-3 w-3" />{r.hour_entry || "—"}</span>
                        <span className="text-xs text-muted-foreground">{pax}</span>
                        <div className="min-w-0">
                          {/* URL absoluta al módulo trips para que el clic-
                              derecho / Cmd+click abra correctamente en pestaña
                              nueva, aunque estemos en otra sub-ruta de la app. */}
                          <a
                            href={`/app/trips?trip=${tripId}&tab=reservas&reserva=${r.id}`}
                            onClick={(e) => {
                              if (e.button === 0 && !e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey) {
                                e.preventDefault();
                                onOpen(r.id);
                              }
                            }}
                            className="flex min-w-0 items-start gap-2 text-left"
                            data-testid={`open-reserva-${r.id}`}
                          >
                            <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${isHotel ? "text-purple-700" : "text-muted-foreground"}`} />
                            <span className={`line-clamp-2 whitespace-normal break-words text-sm font-semibold leading-snug underline underline-offset-2 ${isHotel ? "text-purple-700 decoration-purple-700/30 hover:decoration-purple-700" : "text-primary decoration-primary/30 hover:decoration-primary"}`} title={r.service} data-testid={`reserva-service-${r.id}`}>{r.service}</span>
                          </a>
                          {!isHotel && (inferGroupType(r) || r.td_duration) && (
                            <div className="mt-1 flex flex-wrap items-center gap-1.5 pl-6" data-testid={`reserva-modality-${r.id}`}>
                              {inferGroupType(r) && (
                                <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${inferGroupType(r) === "small_group" ? "bg-amber-500 text-white" : "bg-sky-700 text-white"}`} data-testid={`reserva-group-type-${r.id}`}>
                                  {GROUP_TYPE_LABELS[inferGroupType(r)]}
                                </span>
                              )}
                              {r.td_duration && <span className="text-[11px] text-muted-foreground" data-testid={`reserva-duration-${r.id}`}>{r.td_duration}</span>}
                            </div>
                          )}
                          {isHotel && (r.date_entry || r.date_exit || roomTypesLabel(r) || r.board) && (
                            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 pl-6 text-[11px] text-muted-foreground" data-testid={`reserva-checkinout-${r.id}`}>
                              {(r.date_entry || r.date_exit) && (
                                <span className="tabular-nums">Check-in {fmtShort(r.date_entry) || "—"} → Check-out {fmtShort(r.date_exit) || "—"}{n ? <span className="text-muted-foreground/60"> · {n}n</span> : null}</span>
                              )}
                              {roomTypesLabel(r) && <span className="rounded bg-purple-100 px-1.5 py-0.5 font-medium text-purple-800" data-testid={`reserva-room-type-${r.id}`}>{roomTypesLabel(r)}</span>}
                              {r.board && <span className="rounded bg-purple-100 px-1.5 py-0.5 font-medium text-purple-800" data-testid={`reserva-board-${r.id}`}>{BOARD_LABELS[r.board] || r.board}</span>}
                            </div>
                          )}
                        </div>
                        <span className="min-w-0" onClick={(e) => e.stopPropagation()}>
                          <InlineProviderPicker tripId={tripId} reserva={r} chipColor={sistemaColor} />
                        </span>
                        <span><StateChips r={r} /></span>
                        <span className="text-right text-sm font-bold tabular-nums text-emerald-700" title={reservaQty(r) > 1 ? `Cotizado Real (sin IVA) · ${fmtEUR(r.cost_tax_excl ?? 0)} × ${reservaQty(r)}` : "Cotizado Real (sin IVA)"} data-testid={`reserva-cotizado-${r.id}`}>
                          {fmtEUR(reservaCotizadoTotal(r))}
                          {reservaQty(r) > 1 && <span className="block text-[10px] font-normal text-muted-foreground">{fmtEUR(r.cost_tax_excl ?? 0)} × {reservaQty(r)}</span>}
                        </span>
                        <span className={`text-right text-sm font-bold tabular-nums ${Number(r.invoice_tax_excl) ? "" : "text-muted-foreground/50"}`} title="Coste = factura del proveedor (sin IVA)">{Number(r.invoice_tax_excl) ? fmtEUR(r.invoice_tax_excl) : "—"}</span>
                        <span className="flex justify-center" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted" data-testid={`reserva-menu-${r.id}`}><MoreVertical className="h-4 w-4" /></button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-52">
                              <DropdownMenuItem onClick={() => onOpen(r.id)} data-testid={`menu-edit-${r.id}`}><Pencil className="mr-2 h-4 w-4" />Editar reserva</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openVoucher(trip, r)}><Printer className="mr-2 h-4 w-4" />Imprimir voucher</DropdownMenuItem>
                              <DropdownMenuSub>
                                <DropdownMenuSubTrigger><CalendarClock className="mr-2 h-4 w-4" />Mover a otro día</DropdownMenuSubTrigger>
                                <DropdownMenuSubContent>
                                  {Array.from({ length: Math.max(maxDay, 1) + 1 }, (_, i) => i + 1).filter((d) => d !== day).map((d) => (
                                    <DropdownMenuItem key={d} onClick={() => moveToDay(r, d)} data-testid={`menu-move-${r.id}-${d}`}>Día {d}</DropdownMenuItem>
                                  ))}
                                </DropdownMenuSubContent>
                              </DropdownMenuSub>
                              {(RESERVA_TRANSITIONS[r.status_reserva] || []).length > 0 && (
                                <DropdownMenuSub>
                                  <DropdownMenuSubTrigger><Flag className="mr-2 h-4 w-4" />Cambiar estado</DropdownMenuSubTrigger>
                                  <DropdownMenuSubContent>
                                    {(RESERVA_TRANSITIONS[r.status_reserva] || []).map((s) => (
                                      <DropdownMenuItem key={s} onClick={() => statusMut.mutate({ id: r.id, status_reserva: s })} data-testid={`menu-status-${r.id}-${s}`}>{RESERVA_STATUS_LABELS[s]}</DropdownMenuItem>
                                    ))}
                                  </DropdownMenuSubContent>
                                </DropdownMenuSub>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => onDelete(r)} data-testid={`menu-delete-${r.id}`}><Trash2 className="mr-2 h-4 w-4" />Eliminar</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </span>
                      </div>
                      {r.note ? (
                        <div className="mb-2 ml-[84px] mr-4 rounded-md bg-muted/50 px-2.5 py-1.5 text-xs leading-relaxed text-muted-foreground" data-testid={`reserva-note-${r.id}`}>{r.note}</div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        );
      })}

      {/* Diálogo de preasignación masiva TD */}
      <Dialog open={preasignarOpen} onOpenChange={setPreasignarOpen}>
        <DialogContent className="max-w-2xl" data-testid="preasignar-td-dialog">
          <DialogHeader>
            <DialogTitle>Preasignar plantillas TD</DialogTitle>
            <DialogDescription>
              Recorre las reservas del viaje y asigna la mejor plantilla TD según nombre + tipo + ciudad.
              No sobreescribe descripciones editadas manualmente salvo que actives &quot;sobrescribir&quot;.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md bg-muted/40 p-3 text-sm">
              <div className="mb-1 font-medium">Resumen actual</div>
              <div className="flex gap-4 text-xs">
                <span>Total reservas: <strong>{stats.total}</strong></span>
                <span className="text-emerald-700">Ya con plantilla: <strong>{stats.withPlantilla}</strong></span>
                <span className="text-amber-700">Sin plantilla: <strong>{stats.missing}</strong></span>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-md border p-3">
              <Switch
                id="td-overwrite"
                checked={preasignarOverwrite}
                onCheckedChange={setPreasignarOverwrite}
                data-testid="preasignar-td-overwrite"
              />
              <div className="flex-1">
                <Label htmlFor="td-overwrite" className="text-sm font-medium">
                  Sobrescribir plantillas existentes
                </Label>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Por defecto solo se asigna a reservas <strong>sin plantilla</strong>. Activa esta opción
                  para <strong>reasignar todas</strong> las reservas (útil si las plantillas cambiaron).
                  Ojo: se pisan las ediciones manuales del TD.
                </p>
              </div>
            </div>

            {preasignarResult && (
              <div className="space-y-2 rounded-md border p-3" data-testid="preasignar-td-result">
                <div className="flex items-center gap-2 font-medium">
                  <Check className="h-4 w-4 text-emerald-600" />
                  Resultado
                </div>
                <div className="grid grid-cols-4 gap-2 text-center text-sm">
                  <div className="rounded border p-2">
                    <div className="text-lg font-bold">{preasignarResult.total}</div>
                    <div className="text-xs text-muted-foreground">Reservas</div>
                  </div>
                  <div className="rounded border p-2">
                    <div className="text-lg font-bold text-emerald-700">{preasignarResult.assigned}</div>
                    <div className="text-xs text-muted-foreground">Asignadas</div>
                  </div>
                  <div className="rounded border p-2">
                    <div className="text-lg font-bold text-slate-600">{preasignarResult.kept}</div>
                    <div className="text-xs text-muted-foreground">Sin tocar</div>
                  </div>
                  <div className="rounded border p-2">
                    <div className="text-lg font-bold text-rose-700">{preasignarResult.no_match}</div>
                    <div className="text-xs text-muted-foreground">Sin match</div>
                  </div>
                </div>
                {(preasignarResult.details || []).some((x) => x.action === "no_match") && (
                  <div className="mt-2 max-h-40 space-y-0.5 overflow-y-auto rounded-md bg-amber-50 p-2 text-xs">
                    <div className="font-medium text-amber-900">Sin match (revisar manualmente):</div>
                    {preasignarResult.details
                      .filter((x) => x.action === "no_match")
                      .slice(0, 20)
                      .map((x) => (
                        <div key={x.reserva_id} className="text-amber-800">
                          · {x.service} <span className="text-muted-foreground">(mejor score: {(x.best_score || 0).toFixed(2)})</span>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPreasignarOpen(false)}>Cerrar</Button>
            <Button
              onClick={() => preasignarMut.mutate({ overwrite: preasignarOverwrite })}
              disabled={preasignarMut.isPending}
              data-testid="preasignar-td-run"
            >
              {preasignarMut.isPending ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : (
                <Wand2 className="mr-1.5 h-4 w-4" />
              )}
              {preasignarResult ? "Volver a ejecutar" : "Ejecutar preasignación"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
